/**
 * Progressive enhancement for the Bundler app UI.
 * The app remains fully functional if this file is blocked or delayed.
 */
(function bymsBundlerEnhancements() {
  const targetSelector = '.bundler-target-element, #bundler-target-element';
  const enhancedTargets = new WeakSet();
  const scheduledTargets = new WeakSet();
  const observedContainers = new WeakSet();
  const visibleContainers = new Set();
  let requestedBundleAligned = false;
  let requestedBundlePositioned = false;
  let visitorMoved = false;
  if (location.hash === '#bundler-target-element') {
    const markMoved = () => { visitorMoved = true; };
    window.addEventListener('wheel', markMoved, { once: true, passive: true });
    window.addEventListener('touchstart', markMoved, { once: true, passive: true });
    window.addEventListener('keydown', markMoved, { once: true });
  }
  const bundleVisibilityObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) visibleContainers.add(entry.target);
              else visibleContainers.delete(entry.target);
            });
            document.body.classList.toggle('byms-bundle-in-view', visibleContainers.size > 0);
          },
          { rootMargin: '0px 0px 72px', threshold: 0.08 }
        )
      : null;

  function parseMoney(text) {
    let value = String(text || '').replace(/[^\d,.-]/g, '');

    if (!value) return null;

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      value = lastDot > lastComma ? value.replace(/,/g, '') : value.replace(/\./g, '').replace(',', '.');
    } else if (lastComma > -1) {
      const decimals = value.length - lastComma - 1;
      value = decimals === 2 ? value.replace(',', '.') : value.replace(/,/g, '');
    }

    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : null;
  }

  function getVisibleText(element) {
    if (!element) return '';

    const clone = element.cloneNode(true);
    clone.querySelectorAll('.bndlr-sr-only, .visually-hidden, [aria-hidden="true"]').forEach((node) => node.remove());
    return clone.textContent.trim();
  }

  function getOffer(container) {
    const total = container.querySelector('.bndlr-total-price, .bndlr-mnm-total-price, .bndlr-sectioned-total-price');
    const oldPriceElement = total?.querySelector('.bndlr-old-price, .bndlr-original-price');
    const newPriceElement = total?.querySelector('.bndlr-new-price, .bndlr-discounted-price');
    const oldPriceText = getVisibleText(oldPriceElement);
    const newPriceText = getVisibleText(newPriceElement);
    const oldPrice = parseMoney(oldPriceText);
    const newPrice = parseMoney(newPriceText);

    if (!oldPrice || newPrice === null || newPrice >= oldPrice) return {};

    const saving = oldPrice - newPrice;
    const percentage = Math.max(1, Math.round((saving / oldPrice) * 100));
    const currencyCode = oldPriceText.match(/\b[A-Z]{3}\b/i)?.[0]?.toUpperCase();
    const currencySymbol = oldPriceText.match(/[\u0024\u20ac\u00a3\u00a5\u20b9]/)?.[0];
    const currency = currencyCode || currencySymbol || '';

    return { saving, percentage, currency };
  }

  function setText(element, text) {
    if (element && element.textContent.trim() !== text) element.textContent = text;
  }

  function enhanceContainer(container, containerIndex) {
    const products = Array.from(container.querySelectorAll('.bndlr-product')).filter(
      (product) => !product.closest('.bndlr-mnm-selected-products')
    );

    if (!products.length) return;

    const offer = getOffer(container);
    const count = products.length;
    container.dataset.bymsProductCount = String(count);
    const target = container.closest(targetSelector);
    const isBatmanBundle = target?.dataset.bundle === '847803';
    const title = container.querySelector('.bndlr-bundle-title');
    const description = container.querySelector('.bndlr-bundle-description');
    const warning = container.querySelector('.bndlr-bundle-checkout-warning');
    const total = container.querySelector('.bndlr-total-price, .bndlr-mnm-total-price, .bndlr-sectioned-total-price');
    const action = container.querySelector('.bndlr-add-to-cart, .bndlr-add-bundle-to-cart, .bndlr-add-sectioned-bundle-to-cart');

    // Suppress the product bar only near the bundle purchase button, not
    // throughout a potentially screen-tall bundle product list.
    if (bundleVisibilityObserver && action && !observedContainers.has(action)) {
      observedContainers.add(action);
      bundleVisibilityObserver.observe(action);
    }

    let eyebrow = container.querySelector('.byms-bundler__eyebrow');
    if (!eyebrow && title) {
      eyebrow = document.createElement('p');
      eyebrow.className = 'byms-bundler__eyebrow';
      title.before(eyebrow);
    }
    setText(eyebrow, offer.percentage ? `Better together · Save ${offer.percentage}%` : 'Better together');

    if (title) {
      if (!title.dataset.bymsOriginal) title.dataset.bymsOriginal = title.textContent.trim();
      const originalIsGeneric = /^(get a discount!?|bundle and save!?|frequently bought together)$/i.test(
        title.dataset.bymsOriginal
      );

      if (originalIsGeneric) {
        setText(title, isBatmanBundle ? 'Build your Batman wall.' : 'Make it a set.');
      }

      if (!title.id) title.id = `byms-bundle-title-${containerIndex}`;
      container.setAttribute('role', 'region');
      container.setAttribute('aria-labelledby', title.id);
    }

    if (description) {
      if (!description.dataset.bymsOriginal) description.dataset.bymsOriginal = description.textContent.trim();
      if (/buy these products together/i.test(description.dataset.bymsOriginal)) {
        setText(
          description,
          `${count} pieces, chosen to go together. Select the options for each piece below.`
        );
      }
    }

    products.forEach((product, index) => {
      product.style.setProperty('--byms-bundler-index', String(index));

      let number = product.querySelector(':scope > .byms-bundler__index');
      if (!number) {
        number = document.createElement('span');
        number.className = 'byms-bundler__index';
        number.setAttribute('aria-hidden', 'true');
        product.prepend(number);
      }
      setText(number, String(index + 1).padStart(2, '0'));

      const productTitle = product.querySelector('.bndlr-product-title')?.textContent.trim() || `item ${index + 1}`;
      const image = product.querySelector('img.bndlr-product-image');
      if (image) {
        image.loading = 'lazy';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        // Keep the app's existing 500px source sharp on high-density screens.
        if (image.src.includes('cdn.shopify.com') && image.srcset && !image.srcset.includes(',')) image.removeAttribute('srcset');
      }
      product.querySelectorAll('select').forEach((select) => {
        if (!select.getAttribute('aria-label') || select.getAttribute('aria-label') === 'variant') select.setAttribute('aria-label', `Choose options for ${productTitle}`);
      });
    });

    const innerProducts = container.querySelector('.bndlr-inner-products-container');
    const productRail = Array.from(innerProducts?.children || []).find((child) =>
      child.querySelector(':scope > .bndlr-product')
    );
    productRail?.classList.add('byms-bundler__product-rail');
    container.querySelector('.byms-bundler__scroll-hint')?.remove();

    let savingBadge = total?.querySelector('.byms-bundler__saving');
    if (total && offer.saving && !savingBadge) {
      savingBadge = document.createElement('span');
      savingBadge.className = 'byms-bundler__saving';
      total.append(savingBadge);
    }
    if (savingBadge) savingBadge.hidden = !offer.saving;
    if (savingBadge && offer.saving) {
      const formattedSaving = offer.saving.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setText(savingBadge, `You save ${offer.currency ? `${offer.currency} ` : ''}${formattedSaving}`);
    }

    if (action) {
      const actionText = action.textContent.trim();
      if (!action.dataset.bymsOriginal && actionText) action.dataset.bymsOriginal = actionText;

      if (/^(add to cart|add all \d+ to cart.*|get this set.*)$/i.test(actionText)) {
        setText(action, `Get this set · ${count} pieces`);
        action.setAttribute('aria-label', `Get this set of ${count} pieces`);
      }

      if (!/^(BUTTON|A)$/.test(action.tagName)) {
        action.setAttribute('role', 'button');
        if (!action.hasAttribute('tabindex')) action.setAttribute('tabindex', '0');
      }
    }

    if (warning) {
      if (!warning.dataset.bymsOriginal) warning.dataset.bymsOriginal = warning.textContent.trim();
      if (/discounts? will be applied at checkout/i.test(warning.dataset.bymsOriginal)) {
        setText(
          warning,
          offer.percentage
            ? `Your ${offer.percentage}% bundle saving is applied automatically at checkout.`
            : 'Your bundle saving is applied automatically at checkout.'
        );
      }
    }

    container.querySelector('.byms-bundler__proof')?.remove();
  }

  function enhanceTarget(target) {
    target.querySelectorAll('.bndlr-container').forEach(enhanceContainer);

    // Keep the app's generated IDs intact, while supplying the catalogue's
    // stable destination once its asynchronously loaded widget is ready.
    const container = target.querySelector('.bndlr-container');
    if (target.matches('.bundler-target-element') && !document.getElementById('bundler-target-element')) {
      const anchor = document.createElement('span');
      anchor.id = 'bundler-target-element';
      anchor.setAttribute('aria-hidden', 'true');
      target.before(anchor);
    }
    // The app itself waits for its empty target to enter the viewport before
    // building the widget. Align that target first to let a deep link load it.
    if (target.matches('.bundler-target-element') && location.hash === '#bundler-target-element' && !requestedBundlePositioned) {
      requestedBundlePositioned = true;
      if (!visitorMoved) target.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
    if (container && location.hash === '#bundler-target-element' && !requestedBundleAligned) {
      requestedBundleAligned = true;
      if (!visitorMoved) requestAnimationFrame(() => {
        if (visitorMoved || !container.isConnected) return;
        container.tabIndex = -1;
        container.focus({ preventScroll: true });
        container.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    }

    if (enhancedTargets.has(target)) return;
    enhancedTargets.add(target);

    const observer = new MutationObserver(() => scheduleTarget(target));
    observer.observe(target, { childList: true, subtree: true, characterData: true });
  }

  function scheduleTarget(target) {
    if (!target || scheduledTargets.has(target)) return;
    scheduledTargets.add(target);

    window.requestAnimationFrame(() => {
      scheduledTargets.delete(target);
      enhanceTarget(target);
    });
  }

  function enhanceAll(root = document) {
    if (root instanceof Element && root.matches(targetSelector)) scheduleTarget(root);
    root.querySelectorAll?.(targetSelector).forEach(scheduleTarget);
  }

  function start() {
    const existingTargets = document.querySelectorAll(targetSelector);
    existingTargets.forEach(enhanceTarget);

    if (existingTargets.length) return;

    const loadingObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) enhanceAll(node);
        });
      });
    });

    loadingObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => loadingObserver.disconnect(), 10000);
  }

  document.addEventListener('bundler:bundle_widget_created', () => enhanceAll());
  document.addEventListener('shopify:section:load', (event) => enhanceAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
